import { formatValue } from "../../utils/helpers";
import { MetricMetadata, PercentileData } from "../../services/types";

interface MappableMetadataCardProps {
    metric: PercentileData;
    metadata: MetricMetadata
    width: string;
};

const MappableMetadataCard = ({ metric, metadata, width }: MappableMetadataCardProps) => {

    // Destructure metric properties
    const { name = "Unknown Metric", versions = [] } = metric;

    // Handle versions array and nested properties
    const version: any = versions[0] || {}; // Default to an empty object if no versions exist
    const { unit = "N/A" } = version;

    // Destructure metadata properties
    const { versions: metadataVersions, bin_range_colors = "", tags = [] } = metadata;
    const { polarity = "higher is better" } = metadataVersions[0] || {};

    // Parse bin_range_colors into an array, if empty use default colors
    const defaultBinColors = ["#5d2c70", "#81548A", "#A885A6", "#D1BAC2", "#EEE1E5"];

    const colors = bin_range_colors.trim()
    ? bin_range_colors.split(",").map(color => color.trim())
    : polarity === "higher is better" ? defaultBinColors : defaultBinColors.reverse();

    // Bin fields for percentiles
    const binFields = ["pct0", "pct20", "pct40", "pct60", "pct80", "pct100"];

    // Reverse the binFields if polarity is "higher is better"
    const orderedBinFields = polarity === "higher is better" ? binFields.reverse() : binFields;

    const bins: { min: number, max: number }[] = [];

    for (let i = 0; i < orderedBinFields.length - 1; i++) {
        const min = parseFloat(version[orderedBinFields[i]]);
        const max = parseFloat(version[orderedBinFields[i + 1]]);

        // Reverse min and max if polarity is "higher is better"
        if (polarity === "higher is better") {
            bins.push({ min: max, max: min });
        } else {
            bins.push({ min, max });
        }
    }

    // Check if tags include "mappable_featured" or "default_overlay"
    const isMappableFeatured = tags.some(tag => tag.name === "mappable_featured");
    const isDefaultOverlay = tags.some(tag => tag.name === 'default_overlay');

    return (
        <div className="mx-2 my-2 border rounded-sm p-2 shadow-md">
            <div className="flex flex-col items-start w-full">
                <p className={`text-color-cai-gray text-sm/4 font-bold max-w-56 mt-1`}>{name}</p>
                {/* Percentiles with Color-coded Boxes */}
                <div className={`flex flex-col ${width} mt-4 ml-3`}>
                    {bins.map((bin, index) => (
                        <div className="flex items-center mb-2" key={index}>
                            <div
                                className={`w-4 h-4 border border-color-[#ccc]`}
                                style={{ backgroundColor: colors[index] }}
                            ></div>
                            <p className="text-color-cai-gray text-xs font-bold mx-2 flex-grow">
                                {`${formatValue(bin.min, unit)} - ${formatValue(bin.max, unit)}`}
                            </p>
                        </div>
                    ))}
                </div>
                <div className="flex items-end justify-end w-full">
                    {isDefaultOverlay && <span>🏠</span>} {isMappableFeatured && <span>🌟</span>}
                </div>
            </div>
        </div>
    );
};


export default MappableMetadataCard;
